import { DASHBOARD_TEMPLATE } from './template.ts';
import { renderGrid, sortedForms } from './render.ts';
import { createToast } from './toast.ts';
import * as api from './api.ts';
import type { FormSummary } from './types.ts';
import { signOut } from '../../lib/auth-client.ts';

function must<T extends Element>(container: ParentNode, selector: string): T {
  const el = container.querySelector<T>(selector);
  if (!el) throw new Error(`dashboard: missing required element ${selector}`);
  return el;
}

// Ported verbatim from legacy/dashboard.html's inline <script>. State that
// used to be top-level `var`/implicit globals now lives in this function's
// closure so mountDashboard is idempotent and safe to call repeatedly (each
// call gets its own fresh state; nothing leaks across mounts other than the
// document-level click listener, which the returned cleanup removes).
export function mountDashboard(container: HTMLElement): () => void {
  container.innerHTML = DASHBOARD_TEMPLATE;

  const toast = createToast(container);

  // ── Elements ─────────────────────────────────────────────────
  const searchInput = must<HTMLInputElement>(container, '#search-input');
  const sortSelect = must<HTMLSelectElement>(container, '#sort-select');
  const formsGrid = must<HTMLElement>(container, '#forms-grid');
  const btnNewBlank = must<HTMLElement>(container, '#btn-new-blank');
  const btnImport = must<HTMLElement>(container, '#btn-import');
  const btnSignout = must<HTMLElement>(container, '#btn-signout');
  const importFile = must<HTMLInputElement>(container, '#import-file');
  const renameModal = must<HTMLElement>(container, '#rename-modal');
  const renameInput = must<HTMLInputElement>(container, '#rename-input');
  const renameCancel = must<HTMLElement>(container, '#rename-cancel');
  const renameOk = must<HTMLElement>(container, '#rename-ok');
  const deleteModal = must<HTMLElement>(container, '#delete-modal');
  const deleteCancel = must<HTMLElement>(container, '#delete-cancel');
  const deleteOk = must<HTMLElement>(container, '#delete-ok');

  // ── State ────────────────────────────────────────────────────
  let _forms: FormSummary[] = [];
  let _renameId: string | null = null;
  let _deleteId: string | null = null;

  // ── Search ───────────────────────────────────────────────────
  function applyFilters(): void {
    const q = searchInput.value.toLowerCase().trim();
    const key = sortSelect.value;
    const filtered = q ? _forms.filter((f) => (f.title || '').toLowerCase().includes(q)) : _forms;
    renderGrid(formsGrid, sortedForms(filtered, key));
  }

  // ── Load forms from server ──────────────────────────────────
  async function loadForms(): Promise<void> {
    try {
      _forms = await api.loadForms();
      applyFilters();
    } catch {
      // Plain catch in the legacy code (not an if(r.ok) check) — axios's
      // reject-on-non-2xx naturally collapses into this same branch.
      formsGrid.innerHTML = `<p style="grid-column:1/-1;color:#ef4444;text-align:center;padding:40px">
        ⚠ Server not reachable. Make sure <strong>start.bat</strong> is running.
      </p>`;
    }
  }

  // ── Create new form ──────────────────────────────────────────
  async function createForm(title: string): Promise<void> {
    try {
      const d = await api.createForm(title);
      window.location.href = '/builder?form=' + d.id;
    } catch {
      toast('Could not create form.', 'err');
    }
  }

  // ── Open form ────────────────────────────────────────────────
  function openForm(id: string, tab = ''): void {
    window.location.href = '/builder?form=' + id + (tab ? '&tab=' + tab : '');
  }

  // ── Close all dropdowns ──────────────────────────────────────
  function closeAllMenus(): void {
    container.querySelectorAll('.fc-dropdown.open').forEach((el) => el.classList.remove('open'));
  }

  // ── Rename modal ─────────────────────────────────────────────
  function openRenameModal(id: string): void {
    _renameId = id;
    const form = _forms.find((f) => f.id === id);
    renameInput.value = form ? form.title : '';
    renameModal.style.display = 'flex';
    setTimeout(() => {
      renameInput.focus();
      renameInput.select();
    }, 50);
  }
  async function doRename(): Promise<void> {
    if (!_renameId) return;
    const title = renameInput.value.trim();
    if (!title) {
      toast('Title cannot be empty.', 'err');
      return;
    }
    try {
      await api.renameForm(_renameId, title);
      toast('Form renamed.', 'ok');
      closeRenameModal();
      loadForms();
    } catch {
      toast('Rename failed.', 'err');
    }
  }
  function closeRenameModal(): void {
    renameModal.style.display = 'none';
    _renameId = null;
  }

  // ── Delete modal ─────────────────────────────────────────────
  function openDeleteModal(id: string): void {
    _deleteId = id;
    deleteModal.style.display = 'flex';
  }
  async function doDelete(): Promise<void> {
    if (!_deleteId) return;
    try {
      await api.deleteForm(_deleteId);
      toast('Form deleted.', 'ok');
      closeDeleteModal();
      loadForms();
    } catch {
      toast('Delete failed.', 'err');
    }
  }
  function closeDeleteModal(): void {
    deleteModal.style.display = 'none';
    _deleteId = null;
  }

  // ── Event delegation ─────────────────────────────────────────
  btnNewBlank.addEventListener('click', () => createForm('Untitled Form'));
  btnSignout.addEventListener('click', () => {
    signOut({
      fetchOptions: {
        onSuccess: () => {
          window.location.href = '/login';
        },
      },
    });
  });

  // ── Import form from exported HTML ──────────────────────────
  btnImport.addEventListener('click', () => importFile.click());
  importFile.addEventListener('change', async (e) => {
    const target = e.target as HTMLInputElement;
    const file = target.files?.[0];
    if (!file) return;
    target.value = '';
    try {
      const text = await file.text();
      const marker = text.indexOf('window.__FORM__=');
      if (marker === -1) {
        toast('This HTML file does not contain an exported EMR form.', 'err');
        return;
      }
      const jsonStart = text.indexOf('{', marker);
      if (jsonStart === -1) {
        toast('Could not find form data in file.', 'err');
        return;
      }
      let depth = 0;
      let jsonEnd = -1;
      for (let i = jsonStart; i < text.length; i++) {
        if (text[i] === '{') depth++;
        else if (text[i] === '}') {
          depth--;
          if (depth === 0) {
            jsonEnd = i + 1;
            break;
          }
        }
      }
      if (jsonEnd === -1) {
        toast('Malformed form data in file.', 'err');
        return;
      }
      // Loose typing here matches the legacy code, which never validated
      // the shape of the imported JSON beyond reading a few known fields.
      let formData: any;
      try {
        formData = JSON.parse(
          text
            .slice(jsonStart, jsonEnd)
            .replace(/\\u003c/gi, '<')
            .replace(/\\u003e/gi, '>'),
        );
      } catch {
        toast('Could not parse form data from the file.', 'err');
        return;
      }

      let id: string;
      try {
        const created = await api.createForm(formData.title || 'Imported Form');
        id = created.id;
      } catch {
        toast('Could not create form.', 'err');
        return;
      }

      const saveBody = {
        title: formData.title || 'Imported Form',
        description: formData.description || '',
        coverMedia: formData.coverMedia || null,
        questions: formData.questions || [],
        sections: formData.sections || [],
        captureLocation: formData.captureLocation != null ? !!formData.captureLocation : true,
        logo: formData.logoUrl || '',
        logos: null,
      };
      try {
        await api.saveFormStructure(id, saveBody);
      } catch {
        toast('Form created but could not save structure.', 'err');
        return;
      }

      toast('"' + (formData.title || 'Imported Form') + '" imported successfully!', 'ok');
      loadForms();
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      toast('Import failed: ' + message, 'err');
    }
  });

  formsGrid.addEventListener('click', (e) => {
    const target = e.target as Element;

    // Open via thumbnail click
    const thumb = target.closest('[data-open]');
    if (thumb && !target.closest('.fc-menu-btn') && !target.closest('.fc-dropdown')) {
      openForm((thumb as HTMLElement).dataset.open!);
      return;
    }
    // Three-dot button
    const menuBtn = target.closest('[data-menu]');
    if (menuBtn) {
      e.stopPropagation();
      const id = (menuBtn as HTMLElement).dataset.menu!;
      const dd = must<HTMLElement>(container, '#dd-' + id);
      const alreadyOpen = dd.classList.contains('open');
      closeAllMenus();
      if (!alreadyOpen) {
        dd.classList.add('open');
      }
      return;
    }
    // Dropdown item
    const ddItem = target.closest('[data-action]');
    if (ddItem) {
      e.stopPropagation();
      const action = (ddItem as HTMLElement).dataset.action;
      const id = (ddItem as HTMLElement).dataset.id!;
      closeAllMenus();
      if (action === 'open') openForm(id);
      else if (action === 'rename') openRenameModal(id);
      else if (action === 'responses') openForm(id, 'responses');
      else if (action === 'delete') openDeleteModal(id);
      return;
    }
    // Card body click (info section)
    const card = target.closest('.form-card');
    if (card && !target.closest('.fc-menu-btn') && !target.closest('.fc-dropdown')) {
      openForm((card as HTMLElement).dataset.id!);
    }
  });

  const onDocumentClick = (e: MouseEvent): void => {
    const target = e.target as Element;
    if (!target.closest('.fc-dropdown') && !target.closest('.fc-menu-btn')) closeAllMenus();
  };
  document.addEventListener('click', onDocumentClick);

  searchInput.addEventListener('input', applyFilters);
  sortSelect.addEventListener('change', applyFilters);

  // Rename modal
  renameCancel.addEventListener('click', closeRenameModal);
  renameOk.addEventListener('click', doRename);
  renameInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') doRename();
    if (e.key === 'Escape') closeRenameModal();
  });
  renameModal.addEventListener('click', (e) => {
    if (e.target === renameModal) closeRenameModal();
  });

  // Delete modal
  deleteCancel.addEventListener('click', closeDeleteModal);
  deleteOk.addEventListener('click', doDelete);
  deleteModal.addEventListener('click', (e) => {
    if (e.target === deleteModal) closeDeleteModal();
  });

  // ── Init ─────────────────────────────────────────────────────
  loadForms();

  return () => {
    document.removeEventListener('click', onDocumentClick);
  };
}
